/**
 * El que la pàgina de Reserves ensenya després de cancel·lar (ARQ-2, sprint 4).
 *
 * `DELETE /reservations/:id` torna la reserva amb un `message` que explica què ha passat de
 * debò: cancel·lada al PMS, i si Beds24 ho sap, si només està encuat o si ha fallat, i què ha
 * passat amb els codis dels panys. La pàgina ensenyava un text fix («Reserva cancel·lada
 * correctament») encara que Beds24 no ho hagués acceptat.
 *
 * Aquí només es tria el to i el text; el toast el posa la pàgina.
 */
import { SIMULATED_CANCEL_TOAST } from './beds24-simulated-toast'

export interface CancelResult {
  message?: string | null
  alreadyCancelled?: boolean
  beds24Synced?: boolean
  beds24Queued?: boolean
  beds24SyncError?: string | null
  /** Fora de producció: la cancel·lació NO ha sortit cap a Beds24. A producció no hi és mai. */
  beds24Simulated?: boolean
  locksError?: string | null
}

export interface CancelToast {
  /** `warning` quan la reserva queda cancel·lada aquí però cal fer alguna cosa a mà. */
  kind: 'success' | 'warning' | 'info'
  title: string
  description?: string
}

const FALLBACK_TITLE = 'Reserva cancel·lada correctament'

export function cancelResultToast(result: CancelResult | null | undefined): CancelToast {
  const message = typeof result?.message === 'string' ? result.message.trim() : ''

  // Una resposta sense `message` (backend antic): el text de sempre.
  if (!message) return { kind: 'success', title: FALLBACK_TITLE }

  if (result?.alreadyCancelled) {
    return { kind: 'info', title: 'La reserva ja estava cancel·lada', description: message }
  }

  // Entorn de proves: arriba amb `beds24SyncError` ple, però no és cap error ni cal fer res a mà.
  // Si els panys han fallat, això sí que és de debò i s'hi afegeix.
  if (result?.beds24Simulated === true) {
    return {
      kind: 'warning',
      title: SIMULATED_CANCEL_TOAST.title,
      description: result.locksError
        ? `${SIMULATED_CANCEL_TOAST.description} Els codis dels panys NO s'han pogut revocar: ${result.locksError}.`
        : SIMULATED_CANCEL_TOAST.description,
    }
  }

  if (result?.beds24SyncError || result?.locksError) {
    return {
      kind: 'warning',
      title: 'Reserva cancel·lada al PMS, però cal revisar-la',
      description: message,
    }
  }

  return { kind: 'success', title: 'Reserva cancel·lada', description: message }
}
